import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@tymes4-shared';
import { ScanRegistrationService, TicketScanRegistrationView } from '../../../api';

const REASON_CODE_PREFIX = /^\s*[^:]+:\s*/;

const STATUS_KEYS = {
  granted: 'RELATIONS.TICKET-SCAN-HISTORY.STATUS.GRANTED',
  denied: 'RELATIONS.TICKET-SCAN-HISTORY.STATUS.DENIED',
  'no-data': 'RELATIONS.TICKET-SCAN-HISTORY.STATUS.NO-DATA',
} as const;

type ScanStatusKind = keyof typeof STATUS_KEYS;

function isOkReason(reason: string | null | undefined): boolean {
  if (reason == null) {
    return false;
  }
  return reason.replace(REASON_CODE_PREFIX, '').trim().toLowerCase() === 'ok';
}

@Component({
    selector: 'app-ticket-scan-history',
    templateUrl: './ticket-scan-history.component.html',
    styleUrls: ['./ticket-scan-history.component.scss'],
    standalone: false
})
export class TicketScanHistoryComponent implements OnInit {
  @Input() eventPlacementId: number;

  loading = true;
  scans: TicketScanRegistrationView[] = [];
  expanded = false;

  constructor(
    private scanRegistrationService: ScanRegistrationService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.scanRegistrationService.getTicketScanHistory(this.eventPlacementId).subscribe({
      next: (scans) => this.setScans(scans ?? []),
      error: () => {
        this.snackbar.open(this.translate.instant('GENERIC.SNACKBAR.SOMETHING-WENT-WRONG'), 'GENERIC.SNACKBAR.CLOSE');
        this.setScans([]);
      }
    });
  }

  get latest(): TicketScanRegistrationView | null {
    return this.scans[0] ?? null;
  }

  get expandable(): boolean {
    return this.scans.length > 1;
  }

  toggle(): void {
    if (!this.expandable) {
      return;
    }
    this.expanded = !this.expanded;
  }

  statusKind(scan: TicketScanRegistrationView | null): ScanStatusKind {
    if (scan?.AccessGranted === true) {
      return 'granted';
    }
    if (scan?.AccessGranted === false) {
      return 'denied';
    }
    return 'no-data';
  }

  statusKey(scan: TicketScanRegistrationView | null): string {
    return STATUS_KEYS[this.statusKind(scan)];
  }

  displayReason(reason: string | null | undefined): string {
    if (!reason?.trim() || isOkReason(reason)) {
      return '';
    }
    if (!reason.replace(REASON_CODE_PREFIX, '').trim()) {
      return '';
    }
    return reason;
  }

  private setScans(scans: TicketScanRegistrationView[]): void {
    this.scans = scans;
    this.loading = false;
  }
}
